/** @typedef {import('../models/registryTypes').RegistryEntry} RegistryEntry */
/** @typedef {import('../models/registryTypes').CourtTeam} CourtTeam */
/** @typedef {import('../models/registryTypes').MatchParticipantSnapshot} MatchParticipantSnapshot */

import { entryToSnapshot } from "./normalize";

/**
 * @param {CourtTeam | null | undefined} team
 * @returns {number}
 */
export function teamSlotCount(team) {
  return team?.slots?.length ?? 0;
}

/**
 * @param {CourtTeam | null | undefined} team
 */
export function teamHasSlots(team) {
  return teamSlotCount(team) > 0;
}

/**
 * @param {CourtTeam | null | undefined} team
 * @returns {string[]}
 */
export function teamDisplayNames(team) {
  return (team?.slots || []).map((s) => s.displayName);
}

/**
 * Court markers — full slots (name + avatar + playerId) when available.
 * @param {CourtTeam | null | undefined} team
 * @returns {Array<{ displayName: string, avatar?: string | null, playerId?: string | null }>}
 */
export function teamPlayerSlots(team) {
  const fromSlots = (team?.slots || [])
    .map((s) => {
      const displayName = String(s?.displayName || "").trim();
      if (!displayName) return null;
      return {
        displayName,
        avatar: s?.avatar ? String(s.avatar) : null,
        playerId: s?.playerId ? String(s.playerId) : null,
        registryEntryId: s?.registryEntryId || null,
      };
    })
    .filter(Boolean);
  if (fromSlots.length) return fromSlots;
  // Legacy teams that only stored name strings
  return teamDisplayNames(team)
    .filter(Boolean)
    .map((displayName) => ({
      displayName,
      avatar: null,
      playerId: null,
      registryEntryId: null,
    }));
}

/** Names for court markers / scoreboard (registry slots only). */
export function teamRawPlayerNames(team) {
  return teamDisplayNames(team).filter(Boolean);
}

/**
 * @param {CourtTeam | null | undefined} team
 */
export function teamPlayersLine(team) {
  return teamDisplayNames(team).join(" · ");
}

/**
 * @param {RegistryEntry[]} entries
 * @param {string} entryId
 */
export function findEntry(entries, entryId) {
  return (entries || []).find((e) => e.id === entryId && e.active !== false) || null;
}

/**
 * @param {Object} opts
 * @param {string} opts.name
 * @param {'singles' | 'doubles'} opts.type
 * @param {string[]} opts.entryIds
 * @param {'A' | 'B'} opts.team
 * @param {RegistryEntry[]} opts.registry
 * @returns {CourtTeam}
 */
export function buildCourtTeamFromEntryIds({ name, type, entryIds, team, registry }) {
  const slots = [];
  entryIds.forEach((entryId, position) => {
    const entry = findEntry(registry, entryId);
    if (!entry) return;
    slots.push(entryToSnapshot(entry, team, position));
  });
  return { name, type, slots };
}

/**
 * Guest / label-based court team (managed competition fixtures without room registry).
 * @param {{ name: string, type: 'singles'|'doubles', names: string[], team: 'A'|'B' }} opts
 */
export function buildCourtTeamFromDisplayNames({ name, type, names, team }) {
  const slots = (names || [])
    .map((displayName) => String(displayName || "").trim())
    .filter(Boolean)
    .map((displayName, position) => ({
      registryEntryId: null,
      playerId: `guest_${team}_${position}_${displayName.replace(/\s+/g, "_").slice(0, 24)}`,
      displayName,
      avatar: null,
      team,
      position,
    }));
  return { name, type: type === "doubles" || slots.length > 1 ? "doubles" : "singles", slots };
}

/**
 * @param {string[]} selectedEntryIds
 * @param {string} toggledId
 * @param {number} maxSlots
 * @param {Set<string>} blockedIds Other side entry ids
 */
export function toggleEntrySelection(selectedEntryIds, toggledId, maxSlots, blockedIds) {
  if (blockedIds.has(toggledId)) return selectedEntryIds;
  if (selectedEntryIds.includes(toggledId)) {
    return selectedEntryIds.filter((id) => id !== toggledId);
  }
  if (selectedEntryIds.length >= maxSlots) return selectedEntryIds;
  return [...selectedEntryIds, toggledId];
}

/**
 * Resolve player names from a history/live team when slots were stripped.
 * @param {CourtTeam | { name?: string, players?: string[], slots?: MatchParticipantSnapshot[] } | null | undefined} team
 * @returns {string[]}
 */
function teamFallbackNames(team) {
  const fromSlots = (team?.slots || [])
    .map((s) => s?.displayName)
    .filter(Boolean);
  if (fromSlots.length) return fromSlots;
  if (Array.isArray(team?.players)) {
    return team.players.map((n) => String(n || "").trim()).filter(Boolean);
  }
  const name = String(team?.name || "").trim();
  return name ? [name] : [];
}

/**
 * @param {CourtTeam | object | null | undefined} team
 * @param {'A'|'B'} side
 * @param {RegistryEntry[]} [registryEntries]
 * @returns {MatchParticipantSnapshot[]}
 */
function snapshotsForSide(team, side, registryEntries = []) {
  const slots = (team?.slots || [])
    .map((s, i) => {
      if (!s?.playerId || !s?.displayName) return null;
      return {
        registryEntryId: s.registryEntryId || null,
        playerId: s.playerId,
        displayName: s.displayName,
        avatar: s.avatar ?? null,
        team: side,
        position: Number.isFinite(s.position) ? s.position : i,
      };
    })
    .filter(Boolean);
  if (slots.length) return slots;

  const byName = new Map();
  (registryEntries || []).forEach((e) => {
    if (!e || e.active === false) return;
    const key = String(e.displayName || "")
      .trim()
      .toLowerCase();
    if (key) byName.set(key, e);
  });

  return teamFallbackNames(team).map((displayName, position) => {
    const entry = byName.get(displayName.trim().toLowerCase());
    if (entry) return entryToSnapshot(entry, side, position);
    return {
      registryEntryId: null,
      playerId: `guest_${side}_${position}_${displayName.replace(/\s+/g, "_").slice(0, 24)}`,
      displayName,
      avatar: null,
      team: side,
      position,
    };
  });
}

/**
 * @param {CourtTeam} teamA
 * @param {CourtTeam} teamB
 * @param {RegistryEntry[]} [registryEntries] — used when slots missing (room history backfill)
 * @returns {MatchParticipantSnapshot[]}
 */
export function buildParticipantSnapshots(teamA, teamB, registryEntries = []) {
  return [
    ...snapshotsForSide(teamA, "A", registryEntries),
    ...snapshotsForSide(teamB, "B", registryEntries),
  ];
}
